import {Filter, FilterOption} from 'interfaces';

const CATEGORY_FILTERS = ['business_types', 'category_ids'];

const FILTER_SUFFIX_MAP = new Map([
  ['average_monthly_revenue', 'buttonSuffix.revenue'],
  ['sale_price', 'buttonSuffix.price'],
]);

interface GetCategoryFilterLabelParams {
  selectedOptions: FilterOption[];
  suffix?: string;
}

/**
 * Given a set of selected FilterOption objects for a category, and a suffix, return a label.
 */
function getCategoryFilterLabel({
  suffix,
  selectedOptions,
}: GetCategoryFilterLabelParams): string {
  if (selectedOptions.length === 1) {
    return selectedOptions[0].label;
  }

  return `${selectedOptions.length} ${suffix}`;
}

/**
 * Given two FilterOption objects, return a sort value
 * @param optA FilterOption A
 * @param optB FilterOption B
 */
function filterOptionSort(optA: FilterOption, optB: FilterOption) {
  return (optA.optionId as any) - (optB.optionId as any);
}

/**
 * Given a filter option, return the preferred label
 * @param option The FilterOption to extract the label from
 */
function getOptionLabel(option: FilterOption): string {
  return (option.shortLabel || option.label).toLowerCase();
}

interface RangeFilterLabelDescriptor {
  to?: string;
  from: string;
  unit?: string;
}

const optionLabelSeparator = ' - ';
const selectedOptionSeparator = ', ';

/**
 * Given a FilterOption label for a range filter, return an object that represents this FilterOption's:
 * lower bound, upper bound, and unit.
 * @param label The FilterOption label for a range filter
 */
function getRangeFilterLabelDescriptor(label): RangeFilterLabelDescriptor {
  const labelSplit = label.split(optionLabelSeparator);
  let to;
  let from;
  let unit;

  if (labelSplit.length === 1) {
    const fromSplit = labelSplit[0].split(' ');
    to = fromSplit[0];
    from = null;
    unit = fromSplit[1] || '';
  } else {
    const toSplit = labelSplit[1].split(' ');
    from = labelSplit[0];
    to = toSplit[0];
    unit = toSplit[1] || '';
  }
  return {to, from, unit};
}

/**
 * Given a set of adjacent FilterOption objects, create one label from the least value to highest value respresented.
 * @param options The array of adjacent FilterOption objects
 */
function mergeAdjacentOptionLabels(options: FilterOption[]) {
  if (options.length === 1) {
    return getOptionLabel(options[0]);
  }
  const toLabel = getRangeFilterLabelDescriptor(getOptionLabel(options[options.length - 1]));
  const fromLabel = getRangeFilterLabelDescriptor(getOptionLabel(options[0]));
  const sameUnits = (fromLabel.unit === toLabel.unit);

  const from = (!sameUnits && fromLabel.unit)
    ? `${fromLabel.from} ${fromLabel.unit}`
    : fromLabel.from;

  const to = toLabel.unit ? `${toLabel.to} ${toLabel.unit}` : toLabel.to;

  return `${from}${optionLabelSeparator}${to}`;
}

/**
 * Given a set of FilterOption objects that represent a range of values, where adjacent values can be collapsed into a single label,
 * and where not all FilterOption objects are selected, create a single label that will represent the selected state.
 * @param options  The array of range options
 */
function getRangeFilterLabel(options: FilterOption[]) {
  const selected = options
    .filter((option) => option.checked)
    .sort(filterOptionSort);

  if (selected.length === 1) {
    return getOptionLabel(selected[0]);
  }

  return options
    // sort all filter options by value
    .sort(filterOptionSort)
    // create partitions to represent adjacent selected options
    .reduce((partitions, current) => {
      const lastPartition = partitions[partitions.length - 1];

      if (current.checked) {
        (lastPartition as any).push(current);
      } else if (lastPartition.length) {
        partitions.push([]);
      }

      return partitions;
    }, [[]])
    // remove empty partitions
    .filter((partition) => partition.length)
    // get a merged label for each set of a adjacent range options
    .map(mergeAdjacentOptionLabels)
    // join them together for display
    .join(selectedOptionSeparator);
}

// Memoization cache for filter labels
const LabelCache = new Map();

/**
 * Retrieve a filter label appropriate to the filter and the current selection state of the filter options
 * @param filter The filter for which we're generating a label
 * @param i18n The translation dictionary to use
 */
export function getFilterLabel(filter: Filter, i18n) {
  const isCategory = CATEGORY_FILTERS.includes(filter.type);
  const selectedOptions = filter.options.filter((option) => option.checked);
  const labelCacheKey = `${i18n.locale}:${filter.label}:${selectedOptions.map((option) => option.label).join(':')}`;

  if (LabelCache.has(labelCacheKey)) {
    return LabelCache.get(labelCacheKey);
  }

  let label = '';

  if (selectedOptions.length === 0) {
    label = filter.abbreviatedLabel || filter.label;
  } else if (isCategory) {
    label = getCategoryFilterLabel({
      selectedOptions,
      suffix: i18n.translate('buttonSuffix.categories'),
    });
  } else {
    const rangeSuffix = FILTER_SUFFIX_MAP.has(filter.type)
      ? ` ${i18n.translate(FILTER_SUFFIX_MAP.get(filter.type))}`
      : '';

    label = `${getRangeFilterLabel(filter.options)}${rangeSuffix}`;
  }

  LabelCache.set(labelCacheKey, label);
  return label;
}

/**
 * Given a filter state, return a new updated filter state with a given `filterType`'s `optionId` set to `value`
 * @param filterState The original filter state
 * @param filterType The filter type to update
 * @param optionId The optionId to update
 * @param value The value to update with
 */
export function updateFilterState(filterState: Filter[], filterType: string, optionId: string, value: boolean) {
  return filterState.map((filter) => {
    if (filter.type !== filterType) { return filter; }

    const options = filter.options.map((option) => {
      if (option.optionId !== optionId) { return option; }
      return Object.assign({}, option, {checked: value});
    });

    return Object.assign({}, filter, {options});
  });
}

/**
 * Given a filter state, return a new updated filter state with a all `optionId`s for a given `filterType` reset to false.
 * @param filterState The original filter state
 * @param filterType Optionally specify a filter type to reset, otherwise all filter types will be reset
 */
export function resetFilterState(filterState: Filter[], filterType?: string) {
  return filterState.map((filter) => {
    if (filterType && filter.type !== filterType) {
      return filter;
    }
    const options = filter.options.map((option) => Object.assign({}, option, {checked: false}));
    return Object.assign({}, filter, {options});
  });
}

/**
 * Deep clone a filter state
 * @param filterState The `Filter`s to clone
 */
export function cloneFilters(filterState: Filter[]) {
  return filterState.map((filter) => {
    const options = filter.options.map((option) => Object.assign({}, option));
    return Object.assign({}, filter, {options});
  });
}

/**
 * Test a given filter to see if any options are selected
 * @param filter The `Filter` to test
 */
export function filterHasSelection(filter: Filter) {
  return filter.options.some((option) => option.checked);
}

/**
 * Test a given set of filters to see if any options are selected
 * @param filters The set of `Filter`s to test
 */
export function filtersHaveSelection(filters: Filter[]) {
  return filters.some(filterHasSelection);
}
